import { InteractionStepType } from '@scale/shared/types/chat';
import FieldsBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/FieldsBuilder';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import {
  TextCollectionField,
  TextCollectionFieldType,
} from '@scale/shared/types/params/textcollection';
import OverrideIDBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/OverrideIDBuilder';
import {
  ConditionalStepConfig,
  EnableRTLTextConfig,
  ReviewLevelConfig,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs';
import { StepConfigurationComponentProps } from '../stepDescriptor/types';
import { typeBuilderMap } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemas';

export function TextCollectionConfigurationComponent({
  params: currentParams,
  setParams: setCurrentParams,
  previousParams,
  taskParams,
  property,
  deliveryBuilderConfig,
}: StepConfigurationComponentProps<InteractionStepType.TextCollection>) {
  const showSaveAsSystemMessage =
    property === 'before' &&
    currentParams.params.fields.filter(f => f.type === TextCollectionFieldType.Text).length === 1;
  return (
    <div className="flex flex-col gap-4">
      <StringInputBuilder
        name={'Title'}
        existingValue={currentParams.title}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, title: value })}
        required={true}
      />

      <OverrideIDBuilder
        params={currentParams}
        previousParams={previousParams}
        setParams={setCurrentParams}
      />

      <StringInputBuilder
        name={'Subtitle'}
        existingValue={currentParams.subtitle}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, subtitle: value })}
      />

      <div className="flex flex-col gap-2">
        <div className="font-medium">Fields</div>
        <div className="text-sm text-neutral-500">
          Add fields to collect from the user. You can add as many fields as you want.
        </div>
      </div>
      <FieldsBuilder
        typeBuilderMap={typeBuilderMap}
        name={''}
        stepId={currentParams.id}
        existingValue={currentParams.params.fields}
        updateProperty={(value: TextCollectionField[]) =>
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, fields: value },
          })
        }
        taskTemplateParams={taskParams}
        deliveryBuilderConfig={deliveryBuilderConfig}
      />

      <div className="flex flex-col gap-2">
        <BooleanInputBuilder
          name={'Show Last Response'}
          existingValue={!!currentParams.params.show_last_response}
          updateProperty={(value: boolean) =>
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, show_last_response: value },
            })
          }
        />
        <div className="prose text-sm italic ml-8">
          If checked, the last response in the chat will be added as an attachment to this
          TextCollection step and shown to the tasker. Often used in AttemptCompare pipelines to
          show the latest human response to a reviewer.
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <BooleanInputBuilder
          name={'Enable Inline Comments'}
          existingValue={!!currentParams.params.enable_inline_comments}
          updateProperty={(value: boolean) =>
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, enable_inline_comments: value },
            })
          }
        />
        <div className="prose text-sm italic ml-8">
          If enabled, then users will have the ability to highlight and leave comments on the last
          response if it is shown.
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <EnableRTLTextConfig
          value={currentParams.params.enable_rtl_text}
          onChange={value => {
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, enable_rtl_text: value },
            });
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <BooleanInputBuilder
          name={'Prevent Retroactive Edits'}
          existingValue={!!currentParams.params.prevent_retroactive_edits}
          updateProperty={(value: boolean) =>
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, prevent_retroactive_edits: value },
            })
          }
        />
        <div className="prose text-sm italic ml-8">
          If enabled, then users will be unable to edit the contents of this step after they submit
          this step.
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <BooleanInputBuilder
          name={'Reset Chat History on Step Resubmission'}
          existingValue={!!currentParams.params.reset_history_on_change}
          updateProperty={(value: boolean) =>
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, reset_history_on_change: value },
            })
          }
        />
        <div className="prose text-sm italic ml-8">
          If checked, resubmitting this step (i.e through retroactive edits) will delete the
          existing chat history after the current turn. This should be used when the chat history
          for future turns is dependent on certainText Collection fields in the step.
        </div>
      </div>

      {showSaveAsSystemMessage && (
        <div className="flex flex-col gap-2">
          <BooleanInputBuilder
            name={'Save as System Message'}
            existingValue={!!currentParams.params.save_as_system_message}
            updateProperty={(value: boolean) =>
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, save_as_system_message: value },
              })
            }
          />
          <div className="prose text-sm italic ml-8">
            If enabled, the response from this step will be saved as a system message. Useful when
            users should be able to edit system messages sent to models.
          </div>
        </div>
      )}

      <ReviewLevelConfig currentParams={currentParams} setCurrentParams={setCurrentParams} />
      <ConditionalStepConfig
        currentParams={currentParams}
        setCurrentParams={setCurrentParams}
        previousParams={previousParams}
        taskParams={taskParams}
      />
    </div>
  );
}
